/* eslint-disable no-param-reassign */
import { Checkbox, Collapse, FormControlLabel, Grid } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Custom3DTileset } from "../../types/CesiumMapItems";
import AdjustmentSlider from "./AdjustmentSlider";

interface Props {
  tilesets: Custom3DTileset[];
  onAttenuationSettingsChange: (val: string) => void;
}

export type AttenuationSettings = {
  attenuation: boolean;
  maximumScreenSpaceError: number;
  geometricErrorScale: number;
  maximumAttenuation: number;
  baseResolution: number;
  eyeDomeLighting: boolean;
  eyeDomeLightingStrength: number;
  eyeDomeLightingRadius: number;
};

const AttenuationAdjustment = ({
  tilesets,
  onAttenuationSettingsChange,
}: Props): JSX.Element => {
  const { t } = useTranslation("cesium");
  const [maximumScreenSpaceError, setMaximumScreenSpaceError] = useState(
    tilesets[0].maximumScreenSpaceError,
  );
  const [attenuation, setAttenuation] = useState(
    tilesets[0].pointCloudShading.attenuation,
  );
  const [geometricErrorScale, setGeometricErrorScale] = useState(
    tilesets[0].pointCloudShading.geometricErrorScale,
  );
  const [maximumAttenuation, setMaximumAttenuation] = useState(
    tilesets[0].pointCloudShading.maximumAttenuation,
  );
  const [baseResolution, setBaseResolution] = useState(
    tilesets[0].pointCloudShading.baseResolution,
  );
  const [eyeDomeLighting, setEyeDomeLighting] = useState(
    tilesets[0].pointCloudShading.eyeDomeLighting,
  );
  const [eyeDomeLightingStrength, setEyeDomeLightingStrength] = useState(
    tilesets[0].pointCloudShading.eyeDomeLightingStrength,
  );
  const [eyeDomeLightingRadius, setEyeDomeLightingRadius] = useState(
    tilesets[0].pointCloudShading.eyeDomeLightingRadius,
  );
  const [changed, setChanged] = useState(false);

  // Parse actual values from tileset
  useEffect(() => {
    const shading = tilesets[0].pointCloudShading;
    setMaximumScreenSpaceError(tilesets[0].maximumScreenSpaceError);
    setAttenuation(shading.attenuation);
    setGeometricErrorScale(shading.geometricErrorScale);
    setMaximumAttenuation(shading.maximumAttenuation);
    setBaseResolution(shading.baseResolution);
    setEyeDomeLighting(shading.eyeDomeLighting);
    setEyeDomeLightingStrength(shading.eyeDomeLightingStrength);
    setEyeDomeLightingRadius(shading.eyeDomeLightingRadius);
  }, [tilesets, changed]);

  const handleMaximumScreenSpaceErrorChange = useCallback(
    (val: number) => {
      tilesets.forEach((tileset) => {
        tileset.maximumScreenSpaceError = val;
      });
      setChanged((prev) => !prev);
    },
    [tilesets],
  );

  const handleAttenuationChange = useCallback(
    (val: boolean) => {
      tilesets.forEach((tileset) => {
        tileset.pointCloudShading.attenuation = val;
      });
      setChanged((prev) => !prev);
    },
    [tilesets],
  );

  const handleGeometricErrorScaleChange = useCallback(
    (val: number) => {
      tilesets.forEach((tileset) => {
        tileset.pointCloudShading.geometricErrorScale = val;
      });
      setChanged((prev) => !prev);
    },
    [tilesets],
  );
  const handleMaximumAttenuationChange = useCallback(
    (val: number) => {
      tilesets.forEach((tileset) => {
        tileset.pointCloudShading.maximumAttenuation = val;
      });
      setChanged((prev) => !prev);
    },
    [tilesets],
  );
  const handleBaseResolutionChange = useCallback(
    (val: number) => {
      tilesets.forEach((tileset) => {
        tileset.pointCloudShading.baseResolution = val;
      });
      setChanged((prev) => !prev);
    },
    [tilesets],
  );
  const handleEyeDomeLightingChange = useCallback(
    (val: boolean) => {
      tilesets.forEach((tileset) => {
        tileset.pointCloudShading.eyeDomeLighting = val;
      });
      setChanged((prev) => !prev);
    },
    [tilesets],
  );

  const handleEyeDomeLightingStrengthChange = useCallback(
    (val: number) => {
      tilesets.forEach((tileset) => {
        tileset.pointCloudShading.eyeDomeLightingStrength = val;
      });
      setChanged((prev) => !prev);
    },
    [tilesets],
  );
  const handleEyeDomeLightingRadiusChange = useCallback(
    (val: number) => {
      tilesets.forEach((tileset) => {
        tileset.pointCloudShading.eyeDomeLightingRadius = val;
      });
      setChanged((prev) => !prev);
    },
    [tilesets],
  );

  React.useEffect(() => {
    const settings: AttenuationSettings = {
      attenuation,
      baseResolution,
      eyeDomeLighting,
      eyeDomeLightingRadius,
      eyeDomeLightingStrength,
      geometricErrorScale,
      maximumAttenuation,
      maximumScreenSpaceError,
    };
    const string = JSON.stringify(settings);
    onAttenuationSettingsChange(string);
  }, [
    attenuation,
    baseResolution,
    eyeDomeLighting,
    eyeDomeLightingRadius,
    eyeDomeLightingStrength,
    geometricErrorScale,
    maximumAttenuation,
    maximumScreenSpaceError,
    onAttenuationSettingsChange,
  ]);

  return (
    <Grid item xs={12}>
      <AdjustmentSlider
        value={maximumScreenSpaceError}
        onChange={handleMaximumScreenSpaceErrorChange}
        title={t("maximum-screen-space-error")}
        tooltip={t("maximum-screen-space-error-tooltip")}
        step={1}
        min={1}
        max={64}
      />
      <FormControlLabel
        control={
          <Checkbox
            color="secondary"
            checked={attenuation}
            onChange={(_e, v) => handleAttenuationChange(v)}
          />
        }
        label={t("attenuation")}
      />
      <Collapse in={attenuation}>
        <AdjustmentSlider
          value={geometricErrorScale}
          onChange={handleGeometricErrorScaleChange}
          title={t("geometric-error-scale")}
          step={0.1}
          min={0}
          max={2}
        />
        <AdjustmentSlider
          value={maximumAttenuation}
          onChange={handleMaximumAttenuationChange}
          title={t("maximum-attenuation")}
          step={1}
          min={0}
          max={32}
        />
        <AdjustmentSlider
          value={baseResolution}
          onChange={handleBaseResolutionChange}
          title={t("base-resolution")}
          step={0.1}
          min={0}
          max={10}
        />
        <FormControlLabel
          control={
            <Checkbox
              color="secondary"
              checked={eyeDomeLighting}
              onChange={(_e, v) => handleEyeDomeLightingChange(v)}
            />
          }
          label={t("eye-dome-lighting")}
        />
        <Collapse in={eyeDomeLighting && attenuation}>
          <AdjustmentSlider
            value={eyeDomeLightingStrength}
            onChange={handleEyeDomeLightingStrengthChange}
            title={t("eye-dome-lighting-strength")}
            step={0.1}
            min={0}
            max={10}
          />
          <AdjustmentSlider
            value={eyeDomeLightingRadius}
            onChange={handleEyeDomeLightingRadiusChange}
            title={t("eye-dome-lighting-radius")}
            step={0.1}
            min={0}
            max={10}
          />
        </Collapse>
      </Collapse>
    </Grid>
  );
};

export default AttenuationAdjustment;
